/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo } from 'react';
import { TextField, FormHelperText, FormLabel, Grid } from '@mui/material';
import { GooglePlacesAutocomplete } from '@/components/selects/GooglePlacesAutocomplete';
import {
	ADDRESS,
	CITY,
	COUNTRY,
	SUBURB,
	POSTCODE,
	CLIENT_TYPE,
	DOMESTIC,
	STATE,
} from '../validationSchema';
import { useFormikHelper } from '@/hooks/useFormikHelper';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFormikContext } from 'formik';

export const AddressFields = ({ formik, loading, minimal = false }) => {
	const { values, setFieldValue, getFieldProps } = formik ?? useFormikContext();

	const { getError, getErrorMessage } = useFormikHelper(formik);

	const isDomestic = values?.[CLIENT_TYPE] === DOMESTIC;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const isAustralian = userDetails?.broker?.country === 'AU';

	const required = useMemo(() => {
		return isAustralian
			? true
			: !(values?.isProspect || values?.contactNonRequired) || !values?.prospect2Feature;
	}, [values?.isProspect, isAustralian, values?.contactNonRequired, values?.prospect2Feature]);

	return (
		<>
			{!minimal && (
				<FormLabel required={required} sx={{ width: '100%' }}>
					{`${isDomestic ? 'A' : 'Company a'}ddress`}
				</FormLabel>
			)}
			<Grid container rowSpacing={1} columnSpacing={{ xs: 3, md: 2 }}>
				<Grid size={{ sm: 12, md: !minimal ? 6 : 12 }}>
					<GooglePlacesAutocomplete
						address={values[ADDRESS]}
						error={getError(ADDRESS)}
						helperText={getErrorMessage(ADDRESS)}
						disabled={loading}
						onSelectAddress={(params) => {
							console.log('Selected address', params);
							setFieldValue(ADDRESS, params.addressLine1 ?? '');

							setFieldValue(
								SUBURB,
								(isAustralian ? params.city : params.suburb) ?? '',
							);
							setFieldValue(CITY, isAustralian ? '' : (params.city ?? ''));
							setFieldValue(POSTCODE, params.postcode ?? '');

							if (isAustralian) setFieldValue(STATE, params.state ?? '');

							setFieldValue(COUNTRY, params.country ?? '');
						}}
					/>
					<FormHelperText>Street address</FormHelperText>
				</Grid>
				{!minimal && (
					<>
						<Grid size={{ sm: 12, md: 4 }}>
							<TextField
								slotProps={{ htmlInput: { 'data-testid': 'suburb' } }}
								fullWidth
								required={required}
								{...getFieldProps(SUBURB)}
								error={getError(SUBURB)}
								helperText={getErrorMessage(SUBURB)}
								disabled={loading}
							/>
							<FormHelperText>Suburb</FormHelperText>
						</Grid>
						<Grid size={{ sm: 12, md: 2 }}>
							<TextField
								fullWidth
								name={POSTCODE}
								required={required}
								slotProps={{ htmlInput: { 'data-testid': 'postcode' } }}
								{...getFieldProps(POSTCODE)}
								error={getError(POSTCODE)}
								helperText={getErrorMessage(POSTCODE)}
								disabled={loading}
							/>
							<FormHelperText>Postcode</FormHelperText>
						</Grid>
						{isAustralian && (
							<Grid size={{ xs: 12, md: 6 }}>
								<TextField
									fullWidth
									name={STATE}
									required={required}
									slotProps={{ htmlInput: { 'data-testid': 'state' } }}
									{...getFieldProps(STATE)}
									error={getError(STATE)}
									helperText={getErrorMessage(STATE)}
									disabled={loading}
								/>
								<FormHelperText>State</FormHelperText>
							</Grid>
						)}
						{!isAustralian && (
							<Grid size={{ xs: 12, md: 6 }}>
								<TextField
									fullWidth
									name={CITY}
									required={required}
									slotProps={{ htmlInput: { 'data-testid': 'city' } }}
									{...getFieldProps(CITY)}
									error={getError(CITY)}
									helperText={getErrorMessage(CITY)}
									disabled={loading}
								/>
								<FormHelperText>City</FormHelperText>
							</Grid>
						)}

						<Grid size={{ sm: 12, md: 6 }}>
							<TextField
								fullWidth
								name={COUNTRY}
								required={required}
								slotProps={{ htmlInput: { 'data-testid': 'country' } }}
								{...getFieldProps(COUNTRY)}
								error={getError(COUNTRY)}
								helperText={getErrorMessage(COUNTRY)}
								disabled={loading}
							/>
							<FormHelperText>Country</FormHelperText>
						</Grid>
					</>
				)}
			</Grid>
		</>
	);
};
